import React, { useCallback, useEffect, useRef, useState } from "react";
import { getApiBaseUrl } from "../../config/api";
import "./BannerUpload.css";

const API_URL = getApiBaseUrl();
const MAX_BYTES = 8 * 1024 * 1024;

export default function BannerUploadPopup({ onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const previewUrlRef = useRef(null);

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
    setFile(null);
  }, []);

  useEffect(() => () => clearPreview(), [clearPreview]);

  const handleClose = () => {
    clearPreview();
    if (typeof onClose === "function") onClose();
  };

  const applyFile = (selected) => {
    setError("");
    if (!selected) return;

    if (!String(selected.type || "").startsWith("image/")) {
      setError("Please choose an image file (JPG, PNG, WebP, or GIF).");
      return;
    }
    if (selected.size > MAX_BYTES) {
      setError("Image is too large. Please use a file under 8 MB.");
      return;
    }

    clearPreview();
    const url = URL.createObjectURL(selected);
    previewUrlRef.current = url;
    setFile(selected);
    setPreview(url);
  };

  const handleFileChange = (e) => {
    applyFile(e.target.files?.[0] || null);
    e.target.value = "";
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    applyFile(e.dataTransfer.files?.[0] || null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a banner image first.");
      return;
    }

    const formData = new FormData();
    formData.append("banner", file);

    setUploading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/add_banner.php`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        alert("Banner uploaded successfully!");
        handleClose();
      } else {
        setError(data.error || data.message || "Upload failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong while uploading.");
    } finally {
      setUploading(false);
    }
  };

  const fileLabel = file
    ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB`
    : "";

  return (
    <div
      className="bu-overlay"
      role="presentation"
    >
      <div
        className="bu-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bu-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="bu-close"
          onClick={handleClose}
          aria-label="Close upload banner"
        >
          ×
        </button>

        <header className="bu-header">
          <h2 id="bu-title">Upload Banner</h2>
          <p>Add a wide image for the home carousel. Preview before you upload.</p>
        </header>

        <form className="bu-form" onSubmit={handleUpload}>
          <div
            className={`bu-dropzone${dragging ? " is-dragging" : ""}${preview ? " has-preview" : ""}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            aria-label="Choose banner image"
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="bu-file-input"
              onChange={handleFileChange}
            />

            {preview ? (
              <div className="bu-preview">
                <img src={preview} alt="Banner preview" />
                <div className="bu-preview__meta">
                  <span>{fileLabel}</span>
                  <button
                    type="button"
                    className="bu-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearPreview();
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="bu-dropzone__empty">
                <div className="bu-dropzone__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 16V6m0 0l-3.5 3.5M12 6l3.5 3.5M5 18h14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <strong>Drop an image here</strong>
                <span>or click to browse · JPG, PNG, WebP up to 8 MB</span>
              </div>
            )}
          </div>

          {error ? <p className="bu-error">{error}</p> : null}

          <div className="bu-actions">
            <button
              type="button"
              className="bu-btn bu-btn--ghost"
              onClick={handleClose}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bu-btn bu-btn--primary"
              disabled={!file || uploading}
            >
              {uploading ? "Uploading…" : "Upload banner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
